import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { jiraApi, type JiraProject, type JiraIssueType } from '../../services/api';
import type { Initiative } from '../../types';

interface JiraPushModalProps {
  isOpen: boolean;
  onClose: () => void;
  initiative: Initiative;
  onSuccess: (key: string, url: string) => void;
}

export function JiraPushModal({ isOpen, onClose, initiative, onSuccess }: JiraPushModalProps) {
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [issueTypes, setIssueTypes] = useState<JiraIssueType[]>([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedIssueType, setSelectedIssueType] = useState('');
  const [summary, setSummary] = useState(initiative.title);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingIssueTypes, setLoadingIssueTypes] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load projects on open
  useEffect(() => {
    if (!isOpen) return;
    setSummary(initiative.title);
    setSelectedProject('');
    setSelectedIssueType('');
    setIssueTypes([]);
    setError(null);
    setLoadingProjects(true);
    jiraApi.getProjects()
      .then(setProjects)
      .catch((e) => setError(e.message || 'Failed to load Jira projects'))
      .finally(() => setLoadingProjects(false));
  }, [isOpen, initiative.id]);

  // Load issue types when project selected
  useEffect(() => {
    if (!selectedProject) {
      setIssueTypes([]);
      setSelectedIssueType('');
      return;
    }
    setLoadingIssueTypes(true);
    setSelectedIssueType('');
    jiraApi.getIssueTypes(selectedProject)
      .then(setIssueTypes)
      .catch((e) => setError(e.message || 'Failed to load issue types'))
      .finally(() => setLoadingIssueTypes(false));
  }, [selectedProject]);

  const handlePush = async () => {
    if (!selectedProject || !selectedIssueType) return;
    setPushing(true);
    setError(null);
    try {
      const result = await jiraApi.pushInitiative(initiative.id, {
        projectKey: selectedProject,
        issueTypeId: selectedIssueType,
        summary: summary.trim() || initiative.title,
      });
      onSuccess(result.jiraIssueKey, result.jiraIssueUrl);
    } catch (e: any) {
      setError(e.message || 'Failed to push to Jira');
    } finally {
      setPushing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-200">
          {/* Jira logo */}
          <svg viewBox="0 0 32 32" className="h-5 w-5 flex-shrink-0" aria-hidden="true">
            <defs>
              <linearGradient id="jira-grad" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#2684FF" />
                <stop offset="100%" stopColor="#0052CC" />
              </linearGradient>
            </defs>
            <path d="M15.997 1.156L8.344 8.81 4.01 13.144a1.376 1.376 0 000 1.945l7.653 7.654 4.334 4.334a1.376 1.376 0 001.945 0l4.334-4.334 7.653-7.654a1.376 1.376 0 000-1.945l-4.334-4.334-7.598-7.654zm0 9.932l3.877 3.933-3.877 3.877-3.877-3.877 3.877-3.933z" fill="url(#jira-grad)" />
          </svg>
          <h2 className="text-sm font-semibold text-gray-900">Push to Jira</h2>
          <button type="button" onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Already pushed warning */}
          {initiative.jiraIssueKey && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-xs text-amber-800">
              <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span>Previously pushed as <strong>{initiative.jiraIssueKey}</strong>. Pushing again will create a new Jira issue.</span>
            </div>
          )}

          {/* Project select */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Jira Project</label>
            {loadingProjects ? (
              <p className="text-xs text-gray-500">Loading projects...</p>
            ) : (
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a project...</option>
                {projects.map((p) => (
                  <option key={p.key} value={p.key}>{p.name} ({p.key})</option>
                ))}
              </select>
            )}
          </div>

          {/* Issue type select */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Issue Type</label>
            {loadingIssueTypes ? (
              <p className="text-xs text-gray-500">Loading issue types...</p>
            ) : (
              <select
                value={selectedIssueType}
                onChange={(e) => setSelectedIssueType(e.target.value)}
                disabled={!selectedProject}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select an issue type...</option>
                {issueTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Summary */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Summary</label>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description preview */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description (auto-generated)</label>
            <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 whitespace-pre-line">
              {[
                `Status: ${initiative.status?.name || '-'} | Classification: ${initiative.classification?.name || '-'}`,
                `Timeline: ${initiative.startQuarter}${initiative.endQuarter ? ` → ${initiative.endQuarter}` : ''}`,
                `Owner: ${initiative.user?.name || '-'}`,
                initiative.tags?.length ? `Tags: ${initiative.tags.map((t) => t.tag?.name).filter(Boolean).join(', ')}` : null,
              ].filter(Boolean).join('\n')}
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pushing}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handlePush}
            disabled={!selectedProject || !selectedIssueType || pushing}
          >
            {pushing ? 'Pushing...' : 'Push to Jira'}
          </Button>
        </div>
      </div>
    </div>
  );
}
